import { useCallback, useState } from 'react'

import {
  GenerateTrainingsScormPackagePreviewLinkDocument,
  GenerateTrainingsScormPackagePreviewLinkMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface UseScormPackagePreviewLinkParams {
  scormPackageId: string | undefined
}

export const useScormPackagePreviewLink = ({
  scormPackageId,
}: UseScormPackagePreviewLinkParams) => {
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const [generatePreviewLink, { loading }] = useMutation(
    GenerateTrainingsScormPackagePreviewLinkDocument,
    {
      extractorFn: (data: GenerateTrainingsScormPackagePreviewLinkMutation) =>
        data.trainingsMutations.generatePreviewLinkTrainingsScormPackage,
    }
  )

  const openPreview = useCallback(async () => {
    if (!scormPackageId) {
      setError(i18n.t('trainings.scorm.preview.error'))
      return
    }

    setError(null)

    const result = await generatePreviewLink({
      variables: {
        id: scormPackageId,
        redirectUrl: window.location.origin + window.location.pathname,
      },
    })

    const handled = handleResult(result, () => {
      setError(i18n.t('trainings.scorm.preview.error'))
    })

    if (handled.success) {
      const url = handled.data.scormPackage?.previewUrl
      if (url) {
        setPreviewUrl(url)
      } else {
        setError(i18n.t('trainings.scorm.preview.error'))
      }
    }
  }, [generatePreviewLink, scormPackageId])

  const closePreview = useCallback(() => setPreviewUrl(null), [])

  return { openPreview, closePreview, previewUrl, loading, error }
}
